import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetMyOrdersQuery } from "@/features/api/paymentApi";
import { useGetCourseProgressQuery } from "@/features/api/courseApi";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

import { BookOpen, Video, CheckCircle } from "lucide-react";

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  /* ---------------- ORDERS ---------------- */
  const { data, isLoading } = useGetMyOrdersQuery(undefined, {
    skip: !user,
  });

  const orders = (data?.data || []).filter(
    (o) => o.status === "SUCCESS" && o.productType === "course"
  );

  /* ---------------- COURSE IDS ---------------- */
  const courseIds = useMemo(
    () => orders.map((o) => o.courseId?._id).filter(Boolean),
    [orders]
  );

  /* ---------------- PROGRESS ---------------- */
  const { data: progressRes } = useGetCourseProgressQuery(undefined, {
    skip: !courseIds.length,
  });

  const progressMap = useMemo(() => {
    const map = {};
    progressRes?.forEach((p) => {
      map[p.courseId] = p;
    });
    return map;
  }, [progressRes]);

  /* ---------------- LOADING ---------------- */
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-10">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-72 rounded-xl" />
        ))}
      </div>
    );
  }

  /* ---------------- EMPTY STATE ---------------- */
  if (!orders.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">

        <h2 className="text-3xl font-semibold mb-3">
          No courses purchased yet 🎓
        </h2>

        <p className="text-gray-500 mb-6 text-center max-w-md">
          Start learning today by browsing our available courses.
        </p>

        <Button size="lg" onClick={() => navigate("/courses")}>
          Browse Courses
        </Button>

      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-12">

        <div className="mb-10 text-center">
          <h1 className="text-4xl font-bold">My Learning</h1>
          <p className="text-gray-500 mt-2">
            Continue where you left off 🚀
          </p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

          {orders.map((o) => {
            const course = o.courseId;
            if (!course) return null;

            const percentage = progressMap[course._id]?.percentage || 0;

            let status = "Not Started";
            if (percentage === 100) status = "Completed";
            else if (percentage > 0) status = "In Progress";

            let cta = "Start Course";
            if (percentage > 0) cta = "Resume Learning";
            if (percentage === 100) cta = "Completed";

            return (
              <Card
                key={course._id}
                className="group overflow-hidden rounded-xl border hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                <div className="relative">

                  {percentage === 100 && (
                    <div className="absolute top-3 right-3 bg-green-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 shadow">
                      <CheckCircle className="h-3 w-3" />
                      Completed
                    </div>
                  )}

                  {course.featuredImage ? (
                    <img
                      src={
                        course.featuredImage.startsWith("/uploads")
                          ? `https://fetc.in${course.featuredImage}`
                          : course.featuredImage
                      }
                      className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="h-44 flex items-center justify-center bg-muted">
                      <Video className="h-10 w-10 text-gray-400" />
                    </div>
                  )}
                </div>

                <CardHeader className="pb-2">
                  <h2 className="font-semibold line-clamp-2">
                    {course.title}
                  </h2>
                  <span className="text-xs text-gray-500 mt-1">{status}</span>
                </CardHeader>

                <CardContent className="flex flex-col gap-4 mt-auto">

                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{percentage}%</span>
                    </div>
                    <Progress value={percentage} />
                  </div>

                  <Button
                    onClick={() => navigate(`/courses/${course.slug}/learn`)}
                    className="w-full"
                    disabled={percentage === 100}
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    {cta}
                  </Button>

                </CardContent>
              </Card>
            );
          })}

        </div>
      </div>
    </div>
  );
}